import { IconArrowRight, IconArrowSquareOut, IconCopy, IconXLogo } from "@decibeltrade/react-icons";
import { AffiliateCode } from "@decibeltrade/sdk";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { QRCodeSVG } from "qrcode.react";
import type { ReactNode } from "react";
import { css, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { Clickable } from "@/components/clickable";
import { Skeleton } from "@/components/skeleton";
import { Tooltip } from "@/components/tooltip";
import { useAffiliateAccrualsQuery } from "@/contexts/queries/affiliate-accruals";
import { copy } from "@/utils/copy";
import { shareToX } from "@/utils/share";

import { DividedBanner, VinylBadge } from "../../components";
import { usdCompact } from "../../format";
import { formatKpiAmps } from "../../kpi-format";
import { toTierStanding } from "../../levels/tiers";
import { referralLink, referralLinkDisplay } from "../../referral-link";
import { CommissionsCell } from "./commissions-cell";

// Mode-aware: leads with the active mode — Amps on Points, commission + tier ladder on Commission.
export const HeroBanner = ({
  code,
  account,
  isPointsMode,
  referralAmps,
}: {
  code: AffiliateCode | undefined;
  account: string | undefined;
  isPointsMode: boolean;
  /** All-time referral Amps for the Points headline. */
  referralAmps: number | undefined;
}) => {
  return (
    <DividedBanner
      minH={220}
      /**
       * Measured, not guessed: at 200px a cell breaks "Sub-affiliate" and "Amps share" across
       * two lines, splitting the dashed tooltip underline with them. 240px is the narrowest
       * that holds every label in this banner on one line.
       */
      minCellWidth={240}
      cells={[
        {
          gold: true,
          children: isPointsMode ? (
            <ReferralAmpsCell amps={referralAmps} />
          ) : (
            <CommissionsCell account={account} />
          ),
        },
        {
          children: isPointsMode ? (
            <AmpsRateCell isAffiliate={code?.is_affiliate ?? false} />
          ) : (
            <LevelCell isAffiliate={code?.is_affiliate ?? false} account={account} />
          ),
        },
        { width: 1.3, children: <ReferralLinkCell code={code} /> },
      ]}
    />
  );
};

/** The Points headline: the affiliate's active-mode earnings, not commission. */
const ReferralAmpsCell = ({ amps }: { amps: number | undefined }) => {
  const t = useTranslations("affiliates.overview.hero");

  return (
    <div className={ampsCellStyles}>
      <div className={sectionLabelStyles}>{t("ampsLabel")}</div>
      {amps === undefined ? (
        <Skeleton
          className={css({
            w: "[min(160px, 70cqw)]",
            h: "[clamp(2.15rem, 26cqw, 4rem)]",
            rounded: "200",
          })}
        />
      ) : (
        <div className={ampsAmountStyles}>{formatKpiAmps(amps)}</div>
      )}
      <div className={heroSubStyles}>{t("ampsSub")}</div>
      <div className={heroSubStyles}>{t("ampsAllTime")}</div>
      <Link href="/affiliates/points" className={breakdownLinkStyles}>
        {t("ampsBreakdownLink")}
        <IconArrowRight className={breakdownArrowStyles} />
      </Link>
    </div>
  );
};

// The Points-side rate: a flat 15% (10% for a plain referrer) at every tier.
const AmpsRateCell = ({ isAffiliate }: { isAffiliate: boolean }) => {
  const t = useTranslations("affiliates.overview.hero");
  const l1 = isAffiliate ? AFFILIATE_L1_PERCENT : NORMAL_L1_PERCENT;

  return (
    <div className={stack({ gap: "12", h: "full" })}>
      <div className={sectionLabelStyles}>{t("ampsRateLabel")}</div>
      <div className={heroSubStyles}>{t("ampsRateDescription")}</div>
      <div className={ampsRateRowStyles}>
        <RateStat value={String(l1)} label={t("ampsRateYourReferrals")} hinted />
        <div className={rateDividerStyles} />
        <RateStat value={String(L2_OVERRIDE_PERCENT)} label={t("ampsRateSubAffiliates")} />
      </div>
      <div className={cx(heroSubStyles, css({ mt: "auto" }))}>{t("ampsRateFlat")}</div>
    </div>
  );
};

/**
 * Mirrors AFFILIATE_L1_RATE / NORMAL_L1_RATE / L2_RATE in
 * rust/trading-api/src/http/handle_affiliate_earnings.rs. The L1 rate depends on
 * whether the referrer is a managed affiliate, so it cannot be one constant.
 *
 * These are a share of the referred user's trading **Amps**, not of trading fees:
 * `affiliate_amps_earned = total_hz * rate`, where `total_hz` sums `trading_amps_daily`.
 * The tile was labelled "Fee share", which no part of that math supports — and the
 * on-chain referral fee discount is explicitly off (`REFERRAL_FEE_ENABLED = false`).
 * `primaryLink.rewardShare` already called the same numbers "AMP Share", so the label was
 * also inconsistent with the rest of the portal.
 */
const AFFILIATE_L1_PERCENT = 15;
const NORMAL_L1_PERCENT = 10;
const L2_OVERRIDE_PERCENT = 5;

/**
 * The commission rates used to be three constants here — 25 / 20 / 10 — which happened to match
 * the bronze rung because bronze is where everyone starts. They are gone: the rates come from
 * the ladder the server publishes, so this cell cannot quote a rate the ledger never paid, and
 * a rung change does not need a frontend release.
 *
 * The Amps rates above stay hardcoded on purpose. They are a different system with its own
 * source (`handle_affiliate_earnings.rs`) and are a share of the referred user's Amps, not of
 * trading fees.
 */
const LevelCell = ({
  isAffiliate,
  account,
}: {
  isAffiliate: boolean;
  account: string | undefined;
}) => {
  const t = useTranslations("affiliates.overview.hero");
  const tTierNames = useTranslations("affiliates.tierNames");
  const tLevelsHero = useTranslations("affiliates.levels.hero");
  const accruals = useAffiliateAccrualsQuery({ affiliateAccount: account });
  const standing = toTierStanding(accruals.data);
  // Bound once so the closure does not re-narrow `accruals.data` on every element.
  const commissions = accruals.data;
  const rung = commissions?.tiers.find((r) => r.tier === commissions.current_tier);
  // No `?? "bronze"`. `emptyAccruals()` already covers the affiliate who has accrued nothing
  // with real bronze data, so `undefined` here means the request is in flight or it failed —
  // and bronze's badge, name and 25/5 rates were being shown as fact in both cases. A gold
  // affiliate saw Level 1 at 25% on every first paint.
  const tier = standing?.tier;

  return (
    <div className={stack({ gap: "12", h: "full" })}>
      <div className={sectionLabelStyles}>{t("currentTier")}</div>
      <div className={flex({ alignItems: "center", gap: "12", minW: "0" })}>
        {tier ? (
          <VinylBadge tier={tier} />
        ) : (
          <Skeleton className={css({ w: "[64px]", h: "[64px]", rounded: "full" })} />
        )}
        <div className={stack({ gap: "4", minW: "0" })}>
          <div className={css({ textStyle: "heading.xs", fontWeight: "medium" })}>
            {tier ? (
              tTierNames(tier)
            ) : (
              <Skeleton className={css({ w: "[92px]", h: "[20px]", rounded: "200" })} />
            )}
          </div>
          {/* The figure the tier is derived from, above the gap to the next rung.
              Without it the cell states a gap with no base — "$5.0m more to reach Level 2" does
              not say whether the affiliate stands at $0 or at $4.9m, and the NUV is published
              nowhere else on this page. It is deliberately the *Levels* string rather than a
              second copy under this namespace: same figure, same sentence, so the two screens
              cannot drift into describing it two different ways. */}
          {standing ? (
            <div className={heroSubStyles}>
              {tLevelsHero("nuvSub", {
                amount: usdCompact(standing.nuvUsd),
                days: standing.windowDays,
              })}
            </div>
          ) : null}
          {/* Was "Higher levels unlock in the future". They do not unlock — a tier is a rolling
              30-day band, so it can be reached and lost. What is useful here is the gap.
              Rendered only when there is a standing at all: "no rung above" and "no data" used to
              share this branch, so a failed ledger fetch told the affiliate they had reached the
              highest tier on the strength of nothing. A cell that knows nothing says nothing. */}
          {standing ? (
            <div className={heroSubStyles}>
              {standing.next
                ? t("toNextTier", {
                    amount: usdCompact(standing.next.nuvNeededUsd),
                    tier: tTierNames(standing.next.tier),
                  })
                : t("atTopTier")}
            </div>
          ) : null}
        </div>
      </div>
      {/* Tooltip spells out the basis: "Fee share" alone still reads as a share of the
          affiliate's own fees rather than of what the referred user generated. */}
      <RateRow tooltip={t("feeShareTooltip")}>
        <RateStat
          value={isAffiliate ? asPercent(rung?.rate_l1 ?? 0) : "0"}
          pending={isAffiliate && rung === undefined}
          label={t("feeShareLabel")}
          hinted
        />
        <div className={rateDividerStyles} />
        <RateStat
          value={asPercent(rung?.rate_l2 ?? 0)}
          pending={rung === undefined}
          label={t("subAffiliateLabel")}
        />
      </RateRow>

      {/* The two rows are alternatives, not additive: an affiliate earns in one mode at a
          time (the rewards-mode election). Without the separator the card reads as four
          rates stacking up. */}
      <div className={orSeparatorStyles}>{t("or")}</div>

      {/* Tooltip spells out the basis: "Amps share" alone still reads as a share of the
          affiliate's own Amps rather than of what the referred user earned. */}
      <RateRow tooltip={t("ampsShareTooltip")}>
        <RateStat
          value={String(isAffiliate ? AFFILIATE_L1_PERCENT : NORMAL_L1_PERCENT)}
          label={t("ampsShareLabel")}
          hinted
        />
        <div className={rateDividerStyles} />
        <RateStat value={String(L2_OVERRIDE_PERCENT)} label={t("subAffiliateLabel")} />
      </RateRow>
    </div>
  );
};

/**
 * The rows carried a native `title`, which renders as the OS's own unstyled box at the
 * cursor — in this banner it opened on top of the commissions figure in the cell next door.
 * The app tooltip is themed, arrowed, and tappable on touch.
 */
const RateRow = ({ tooltip, children }: { tooltip: string; children: ReactNode }) => (
  <Tooltip content={tooltip}>
    <div className={rateRowStyles}>{children}</div>
  </Tooltip>
);

/**
 * A rate as a percentage, keeping a half point when there is one.
 *
 * This used to be `Math.round(rate * 100)`, which was fine while every rate was a whole percent
 * and became wrong the day silver's L2 moved to 7.5%: rounding rendered it as 8%, a rate the
 * ledger never pays. One decimal, trailing zero dropped, so 25% stays "25" and 7.5% stays "7.5".
 */
function asPercent(rate: number): string {
  const percent = rate * 100;
  return Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
}

/** `hinted` dashes the label the way `TooltipText` does, so the row reads as hoverable. */
const RateStat = ({
  value,
  label,
  hinted,
  pending,
}: {
  value: string;
  label: string;
  hinted?: boolean;
  /** The rate is not known yet. Renders a skeleton rather than a rate nobody is paid. */
  pending?: boolean;
}) => (
  <div className={stack({ gap: "4", flex: "1", minW: "0" })}>
    {pending ? (
      <Skeleton className={css({ w: "[56px]", h: "[28px]", rounded: "200" })} />
    ) : (
      <div className={css({ textStyle: "highlight.lg", color: "text.primary" })}>{value}%</div>
    )}
    <div className={cx(rateLabelStyles, hinted && hintedLabelStyles)}>{label}</div>
  </div>
);

const ReferralLinkCell = ({ code }: { code: AffiliateCode | undefined }) => {
  const t = useTranslations("affiliates.overview.hero");
  const tLink = useTranslations("affiliates.primaryLink");
  const tGeneral = useTranslations("general");

  if (!code) {
    return (
      <div className={stack({ gap: "12", h: "full" })}>
        <div className={sectionLabelStyles}>{t("yourReferralLink")}</div>
        <div className={heroSubStyles}>{t("noCodeProvisioned")}</div>
      </div>
    );
  }

  const link = referralLink(code.referral_code);

  const handleCopyLink = () => {
    void copy({
      value: link,
      successMessage: tLink("linkCopied"),
      errorMessage: tLink("failedToCopyLink"),
      tGeneral,
    });
  };

  return (
    <div className={stack({ gap: "12", h: "full" })}>
      <div className={sectionLabelStyles}>{t("yourReferralLink")}</div>
      <div className={flex({ gap: "16", alignItems: "flex-start", minW: "0" })}>
        <div className={qrTileStyles}>
          <QRCodeSVG value={link} size={QR_SIZE} bgColor="#FFFFFF" fgColor="#0B0B0B" />
        </div>
        <div className={stack({ gap: "8", flex: "1", minW: "0" })}>
          <div className={css({ textStyle: "subheading.2xs", color: "text.primary" })}>
            {code.referral_code}
          </div>

          <div
            className={css({
              textStyle: "body.md",
              color: "text.tertiary",
              wordBreak: "break-all",
            })}
          >
            {referralLinkDisplay(code.referral_code)}
          </div>
          <div className={flex({ gap: "4", alignItems: "center" })}>
            <Clickable
              className={cx(actionButtonStyles, copyButtonStyles)}
              onClick={handleCopyLink}
              ariaLabel={t("copyReferralLink")}
            >
              <IconCopy className={css({ w: "16", h: "16", color: "text.secondary" })} />
              {tLink("copyLink")}
            </Clickable>

            <Clickable
              className={actionButtonStyles}
              onClick={() => window.open(link, "_blank", "noopener,noreferrer")}
              ariaLabel={t("openReferralLink")}
            >
              <IconArrowSquareOut className={css({ w: "16", h: "16", color: "text.secondary" })} />
            </Clickable>
          </div>
        </div>
      </div>
      {/* The cell's job is distribution, and copy-to-clipboard only helps someone already
          composing a post. This is also what fills the cell: it is the widest of the three and
          carried the least, so the usage line sat alone at the bottom of an empty half.
          Deliberately not another stats strip — the KPI band above already counts this network,
          and repeating it here would have been decoration wearing data's clothes. */}
      <Clickable
        className={shareButtonStyles}
        onClick={() => shareToX(t("shareTweetTemplate", { link }))}
        ariaLabel={t("shareOnX")}
      >
        <IconXLogo className={css({ w: "16", h: "16" })} />
        {t("shareOnX")}
      </Clickable>
      <div className={cx(heroSubStyles, css({ mt: "auto" }))}>
        {t("usageLine", {
          uses: code.usage_count.toLocaleString(),
          maxUsers: code.max_usage.toLocaleString(),
        })}
      </div>
    </div>
  );
};

/**
 * 120, up from 80. The QR is the one thing in this banner that is used off-screen — held up on a
 * stream or scanned off someone's laptop — and at 80 it read as an icon rather than something to
 * point a phone at. It is also what gives this cell a body: 120 + padding lines the tile up with
 * the code, link and both action rows beside it, instead of leaving a half-empty cell.
 */
const QR_SIZE = 120;

/**
 * Full width rather than a third chip beside Copy/Open: it is the only action here that reaches
 * people who are not already looking at the affiliate's screen, and a chip would have read as
 * equal in weight to the icon-only open button.
 */
const shareButtonStyles = flex({
  alignItems: "center",
  justifyContent: "center",
  gap: "8",
  py: "8",
  bg: "background.secondary",
  rounded: "300",
  textStyle: "highlight.sm",
  color: "text.secondary",
  transitionPreset: "colors",
  _hover: { bg: "background.canvas", color: "text.primary" },
});

/** QR modules need a light field to stay scannable, so this tile stays white in both themes. */
const qrTileStyles = css({
  bg: "[#FFFFFF]",
  p: "8",
  rounded: "300",
  lineHeight: "[0]",
  flexShrink: "0",
});

const copyButtonStyles = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "4",
  px: "8",
  textStyle: "highlight.sm",
  color: "text.secondary",
});

const rateRowStyles = flex({
  mt: "auto",
  alignItems: "stretch",
  gap: "12",
  bg: "background.secondary",
  rounded: "300",
  px: "12",
  py: "8",
  cursor: "help",
});

const rateLabelStyles = css({ textStyle: "body.sm", color: "text.tertiary" });

const hintedLabelStyles = css({
  textDecoration: "underline",
  textDecorationStyle: "dashed",
  textUnderlineOffset: "4",
});

const orSeparatorStyles = css({
  textStyle: "body.sm",
  color: "text.tertiary",
  textTransform: "uppercase",
  letterSpacing: "[1px]",
  alignSelf: "center",
  // Negative pull: the rows carry their own padding, and without it the word floats
  // in a gap twice the size of the rows' internal spacing.
  my: "-4",
});

const rateDividerStyles = css({
  w: "[1px]",
  alignSelf: "stretch",
  bg: "border.primary",
  flexShrink: "0",
});

const sectionLabelStyles = css({
  textStyle: "highlight.sm",
  letterSpacing: "[1px]",
  textTransform: "uppercase",
  color: "text.tertiary",
});

const heroSubStyles = css({
  textStyle: "body.md",
  color: "text.tertiary",
});

const actionButtonStyles = css({
  bg: "background.canvas",
  p: "4",
  rounded: "200",
  transitionPreset: "colors",
  _hover: { bg: "background.secondary" },
});

// Mirrors the commissions cell so the two modes swap without the banner jumping.
const ampsCellStyles = stack({ gap: "12", h: "full", containerType: "inline-size" });

const ampsAmountStyles = css({
  textStyle: "display.lg",
  fontSize: "[clamp(1.75rem, 21cqw, 3.25rem)]",
  fontWeight: "medium",
  color: "text.primary",
});

const breakdownLinkStyles = flex({
  mt: "auto",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8",
  pt: "12",
  borderTopWidth: "100",
  borderTopStyle: "solid",
  borderTopColor: "border.primary",
  textStyle: "highlight.sm",
  color: "text.secondary",
  transitionPreset: "colors",
  _hover: { color: "text.primary", "& svg": { transform: "translateX(2px)" } },
});

const breakdownArrowStyles = css({
  w: "16",
  h: "16",
  flexShrink: "0",
  transitionPreset: "transform",
});

// Like rateRowStyles but without mt:auto, so a note can sit below the rate.
const ampsRateRowStyles = flex({
  alignItems: "stretch",
  gap: "12",
  bg: "background.secondary",
  rounded: "300",
  px: "12",
  py: "8",
});
